import { DailyBusinessHours } from '../models/DailyBusinessHours'

const getDateStringFromRange = (
  range: [Date | null, Date | null],
  numberOfDaysToRunAd: number | null,
) => {
  const [start, end] = range
  if (numberOfDaysToRunAd) return `${numberOfDaysToRunAd} days`
  if (!start) return 'No Dates Selected'

  return end
    ? `${start?.toLocaleDateString()} - ${end?.toLocaleDateString()}`
    : start?.toLocaleDateString()
}

const getBusinessDaysInfoFromDateRange = (
  range: [Date | null, Date | null],
  businessHours: Record<string, DailyBusinessHours>,
  numberOfDaysToRunAd: number | null,
) => {
  if (numberOfDaysToRunAd != null) {
    const startDate = new Date()
    const closedDays = countClosedDaysForNextDays(
      startDate,
      businessHours,
      numberOfDaysToRunAd,
    )
    return {
      totalDays: numberOfDaysToRunAd,
      closedDays: closedDays,
      openDays: numberOfDaysToRunAd - closedDays,
    }
  }

  if (range[0] == null) {
    return {
      totalDays: 0,
      closedDays: 0,
      openDays: 0,
    }
  }

  if (range[1] == null) {
    const dayName = getDayName(range[0]!.getDay())
    const closed = businessHours[dayName].closed ? 1 : 0
    return {
      totalDays: 1,
      closedDays: closed,
      openDays: 1 - closed,
    }
  }

  const diffInTime = range[1]!.getTime() - range[0]!.getTime()
  const totalDays = Math.ceil(diffInTime / (1000 * 3600 * 24))
  let closedDays = 0

  for (let i = 0; i < totalDays; i++) {
    const currentDate = new Date(range[0]!)
    currentDate.setDate(currentDate.getDate() + i)
    const currentDayName = getDayName(currentDate.getDay())

    if (businessHours[currentDayName].closed) {
      closedDays++
    }
  }

  const openDays = totalDays - closedDays

  return {
    totalDays,
    closedDays,
    openDays,
  }
}

function countClosedDaysForNextDays(
  startDate: Date,
  businessHours: Record<string, DailyBusinessHours>,
  numberOfDays: number,
) {
  let closedDaysCount = 0

  for (let i = 0; i < numberOfDays; i++) {
    const date = new Date(startDate)
    date.setDate(date.getDate() + i)
    const weekdayName = getDayName(date.getDay())

    if (businessHours[weekdayName].closed) {
      closedDaysCount++
    }
  }

  return closedDaysCount
}

const getDayName = (dayIndex: number): string => {
  const daysOfWeek = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ]
  return daysOfWeek[dayIndex]
}

const getCostFromDateRange = (
  dailyCost: number,
  range: [Date | null, Date | null],
  businessHours: Record<string, DailyBusinessHours>,
  numberOfDaysToRunAd: number | null,
  weeklyCost: number,
  monthlyCost: number,
) => {
  // If we have a predefined number of days, use the appropriate pricing model
  if (numberOfDaysToRunAd) {
    switch (numberOfDaysToRunAd) {
      case 7: // 1 week
        return weeklyCost
      case 14: // 2 weeks
        return weeklyCost * 2
      case 30: // 1 month
        return monthlyCost
      case 90: // 3 months
        return monthlyCost * 3
      default:
        return monthlyCost
    }
  }

  const info = getBusinessDaysInfoFromDateRange(
    range,
    businessHours,
    numberOfDaysToRunAd,
  )
  return info.openDays * dailyCost
}

const stripTime = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

const isBeforeToday = (date: Date | null): boolean => {
  if (date == null) return false
  const today = stripTime(new Date())
  const strippedDate = stripTime(date)
  return strippedDate < today
}

const setToMidnight = (date: Date): Date => {
  const localDate = new Date(date)
  localDate.setHours(0, 0, 0, 0)
  console.log('localDate', localDate)
  return localDate
}

const convertToEndOfDay = (date: Date): Date => {
  const localDate = new Date(date)
  localDate.setHours(23, 59, 59, 999)
  console.log('localDate', localDate)
  return localDate
}

const convertServerDateToLocal = (date: string | Date | null): Date | null => {
  if (!date) return null
  // If it's a string and doesn't end with Z, add Z to treat it as UTC
  if (typeof date === 'string' && !date.endsWith('Z')) {
    return new Date(date + 'Z')
  }
  return new Date(date)
}

export const toLocalDateTimeString = (date: Date): string => {
  // Format as YYYY-MM-DDTHH:mm:ss using simple toString approach
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
}

export {
  convertServerDateToLocal,
  convertToEndOfDay,
  getBusinessDaysInfoFromDateRange,
  getCostFromDateRange,
  getDateStringFromRange,
  getDayName,
  isBeforeToday,
  setToMidnight,
}
